import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Info, Music, Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { downloadMasterTrack } from "../../../../../redux/actions/audioEngineer";
import DropdownMenu from "../../../../../components/DropdownMenu";
import { extractFileName } from "../../../../../helper/extractFileName";
import { AudioEngineerPackModal } from "../AudioEngineerPackModal";

export default function PreviewSection({ demoMode = false, demoModeMaster = false }) {
  const dispatch = useDispatch()
  const audioRef = useRef(null);
  const svgRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false)
  const [showAudioEngineerModal, setShowAudioEngineerModal] = useState(false);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // from audio when NOT dragging
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isPlayInitiated, setIsPlayInitiated] = useState(false);

  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState(null); // temp progress while dragging
  const [wasPlayingBeforeDrag, setWasPlayingBeforeDrag] = useState(false);

  const audioEngineerReducer = useSelector((state) => state.audioEngineer);
  const { fileName, streamUrl, inputUri, originalUrl, format } = audioEngineerReducer?.previewStatus || {};

  let previewArray = [];
  if (Array.isArray(audioEngineerReducer?.preview)) {
    previewArray = audioEngineerReducer.preview;
  } else if (Array.isArray(audioEngineerReducer?.preview?.data)) {
    previewArray = audioEngineerReducer.preview.data;
  } else if (audioEngineerReducer?.preview && typeof audioEngineerReducer.preview === 'object') {
    previewArray = [audioEngineerReducer.preview];
  } else {
    previewArray = [];
  }
  const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(0);
  const selectedTrack = Array.isArray(previewArray) && previewArray.length > 0 ? previewArray[selectedPreviewIndex] : null;
  const previewUrlFromArray = previewArray.length > 0
    ? (
      previewArray[selectedPreviewIndex]?.data?.masteredUrl
      || previewArray[selectedPreviewIndex]?.data?.previewUrl
      || previewArray[selectedPreviewIndex]?.previewUrl
    )
    : null;
  const previewUrl = previewUrlFromArray || audioEngineerReducer?.previewStatus?.previewUrl || null;
  const originalUrlFromArray = previewArray.length > 0
    ? (
      previewArray[selectedPreviewIndex]?.originalUrl
    )
    : null;
  const originalUrls = originalUrlFromArray || null

  // UI controls 
  const [isMaster, setIsMaster] = useState(true);
  const [genre, setGenre] = useState(audioEngineerReducer?.previewStatus?.style || "ROCK_INDIE");
  const [loudness, setLoudness] = useState(audioEngineerReducer?.previewStatus?.loudness || "MEDIUM");
  const [sampleRate, setSampleRate] = useState(audioEngineerReducer?.previewStatus?.sampleRate || "48000");
  const allMastered =
    (Array.isArray(previewArray) && previewArray.length > 0
      ? previewArray.every(item => item?.data?.isMastered === true)
      : false);
  console.log('PreviewSection render', { previewArray, selectedTrack, previewUrlFromArray, previewUrl, originalUrls, allMastered });

  // SVG geometry
  const VIEWBOX = 220;
  const CX = VIEWBOX / 2;
  const CY = VIEWBOX / 2;
  const RADIUS = 90;

  const lastSeekRef = useRef(0);
  const IGNORE_AFTER_SEEK_MS = 250;
  const handledPlayRef = useRef(false);

  //Audio event wiring
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMeta = () => {
      setDuration(audio.duration || 0);
      setIsAudioLoading(false);
    };

    const onCanPlay = () => {
      setIsAudioLoading(false);
    };

    const onPlay = () => {
      setIsPlayInitiated(false);
    };

    const onLoadStart = () => {
      setIsAudioLoading(true);
    };

    const onTimeUpdate = () => {
      const now = Date.now();
      if (now - lastSeekRef.current < IGNORE_AFTER_SEEK_MS) return;

      const t = audio.currentTime || 0;
      setCurrentTime(t);
      if (!isDragging && audio.duration) {
        setProgress((t / audio.duration) * 100);
      }
    };

    const onEnded = () => {
      setIsPlaying(false);
      setIsPlayInitiated(false);
      setProgress(0);
    };

    const onError = (e) => {
      console.error("Audio error", e);
      setError("Failed to load audio");
      setIsPlaying(false);
    };

    audio.addEventListener("loadedmetadata", onLoadedMeta);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("loadstart", onLoadStart);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMeta);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("loadstart", onLoadStart);
      audio.addEventListener("play", onPlay);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [isDragging, streamUrl, originalUrl, previewUrl]);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setError(null);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    if (previewUrl) {
      try {
        audio.src = previewUrl;
        audio.load();
        const playNow = !!audioEngineerReducer?.preview?.playNow;
        if (playNow && !handledPlayRef.current) {
          audio.play().then(() => {
            setIsPlaying(true);
          }).catch((err) => {
            console.debug('Autoplay attempt failed', err);
            setIsPlaying(false);
          });
          handledPlayRef.current = true;
        }
      } catch (e) {
        console.warn('Failed to set audio.src', e);
      }
    }
  }, [previewUrl]);

  useEffect(() => {
    handledPlayRef.current = false;
  }, [audioEngineerReducer?.preview]);

  useEffect(() => {
    if (audioEngineerReducer?.previewStatus) {
      setGenre(audioEngineerReducer.previewStatus.style || "ROCK_INDIE");
      setLoudness(audioEngineerReducer.previewStatus.loudness || "MEDIUM");
      setSampleRate(audioEngineerReducer.previewStatus.sampleRate || "48000");
    }
  }, [audioEngineerReducer?.previewStatus]);

  useEffect(() => {
    if (!Array.isArray(previewArray) || previewArray.length === 0) {
      setSelectedPreviewIndex(0);
      return;
    }
    if (selectedPreviewIndex >= previewArray.length) setSelectedPreviewIndex(0);
  }, [previewArray]);

  useEffect(() => {
    if (Array.isArray(previewArray) && previewArray.length > 0) {
      setSelectedPreviewIndex(0);
    }
  }, [audioEngineerReducer?.preview]);

  const downloadAudioFile = (url, format, fileName) => {
    const reqBody = {
      url: url,
      format: format,
      fileName: fileName,
    }
    dispatch(downloadMasterTrack(reqBody, setIsDownloading))
  }

  const getDownloadItems = (track) => [
    {
      label: "Download as WAV file",
      icon: <Music size={16} />,
      onClick: () => {
        const url = track?.masteredUrl || previewUrl || audioEngineerReducer?.previewStatus?.previewUrl || null;
        const name = track?.filename || track?.data?.filename || "mastered";
        return downloadAudioFile(url, "wav", name);
      },
    },
    {
      label: "Download as MP3 file",
      icon: <Music size={16} />,
      onClick: () => {
        const url = track?.masteredUrl || previewUrl || audioEngineerReducer?.previewStatus?.previewUrl || null;
        const name = track?.filename || track?.data?.filename || "mastered";
        return downloadAudioFile(url, 'mp3', name);
      },
    },
    {
      label: "Download as M4A file",
      icon: <Music size={16} />,
      onClick: () => {
        const url = track?.masteredUrl || previewUrl || audioEngineerReducer?.previewStatus?.previewUrl || null;
        const name = track?.filename || track?.data?.filename || "mastered";
        return downloadAudioFile(url, 'm4a', name);
      },
    },
    {
      label: "Download as AAC file",
      icon: <Music size={16} />,
      onClick: () => {
        const url = track?.masteredUrl || previewUrl || audioEngineerReducer?.previewStatus?.previewUrl || null;
        const name = track?.filename || track?.data?.filename || "mastered";
        return downloadAudioFile(url, 'aac', name);
      },
    },
    {
      label: "Download as OGG file",
      icon: <Music size={16} />,
      onClick: () => {
        const url = track?.masteredUrl || previewUrl || audioEngineerReducer?.previewStatus?.previewUrl || null;
        const name = track?.filename || track?.data?.filename || "mastered";
        return downloadAudioFile(url, 'ogg', name);
      },
    },
    {
      label: "Download as AIFF file",
      icon: <Music size={16} />,
      onClick: () => {
        const url = track?.masteredUrl || previewUrl || audioEngineerReducer?.previewStatus?.previewUrl || null;
        const name = track?.filename || track?.data?.filename || "mastered";
        return downloadAudioFile(url, 'aiff', name);
      },
    }
  ];

  // Play / Pause
  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (audio.paused) {
        setIsPlayInitiated(true);
        await audio.play();
        setIsPlaying(true);
      } else {
        audio.pause();
        setIsPlaying(false);
      }
    } catch (err) {
      setIsPlayInitiated(true);
      console.error("Playback error", err);
      // setError("Playback failed");
    }
  };

  const clientToProgress = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;

    let angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
    angleDeg += 90;
    if (angleDeg < 0) angleDeg += 360;
    return (angleDeg / 360) * 100;
  };

  const startDrag = (clientX, clientY) => {
    const audio = audioRef.current;
    const initial = clientToProgress(clientX, clientY) ?? 0;

    setWasPlayingBeforeDrag(Boolean(audio && !audio.paused && !audio.ended));
    try {
      if (audio && !audio.paused) audio.pause();
    } catch (e) {
      // ignore
    }

    setDragProgress(initial);
    setIsDragging(true);

    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("mouseup", onPointerUp);
    window.addEventListener("touchmove", onPointerMove, { passive: false });
    window.addEventListener("touchend", onPointerUp);

    document.body.style.userSelect = "none";
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    const isTouch = e.type === "touchstart";
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    startDrag(clientX, clientY);
  };

  const onPointerMove = (e) => {
    if (!isDragging) return;
    if (e.cancelable) e.preventDefault();
    const isTouch = e.type === "touchmove";
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;

    const p = clientToProgress(clientX, clientY);
    if (p != null) setDragProgress(p);
  };

  const onPointerUp = (e) => {
    if (!isDragging) return;
    if (e.cancelable) e.preventDefault();

    let final = dragProgress;
    if (final == null) {
      const isTouch = e.type === "touchend";
      if (!isTouch && e.clientX != null) {
        final = clientToProgress(e.clientX, e.clientY) ?? final;
      }
    }
    if (final == null) final = 0;

    const audio = audioRef.current;
    if (audio && audio.duration && !isNaN(audio.duration)) {
      const newTime = (final / 100) * audio.duration;
      lastSeekRef.current = Date.now();
      try {
        audio.currentTime = newTime;
      } catch (err) {
        console.warn("Seeking failed:", err);
      }

      setProgress(final);
      setCurrentTime(newTime);
    }

    if (audio && wasPlayingBeforeDrag) {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    } else {
      setIsPlaying(Boolean(audio && !audio.paused));
    }

    setIsDragging(false);
    setDragProgress(null);
    setWasPlayingBeforeDrag(false);

    // cleanup listeners
    window.removeEventListener("mousemove", onPointerMove);
    window.removeEventListener("mouseup", onPointerUp);
    window.removeEventListener("touchmove", onPointerMove);
    window.removeEventListener("touchend", onPointerUp);

    document.body.style.userSelect = "";
  };

  const activeProgress = dragProgress != null ? dragProgress : progress || 0;

  const pointerAngle = (activeProgress / 100) * 360 - 90;
  const pointerRad = (pointerAngle * Math.PI) / 180;
  const pointerX = CX + RADIUS * Math.cos(pointerRad);
  const pointerY = CY + RADIUS * Math.sin(pointerRad);

  // cleanup listeners on unmount just in case
  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onPointerMove);
      window.removeEventListener("mouseup", onPointerUp);
      window.removeEventListener("touchmove", onPointerMove);
      window.removeEventListener("touchend", onPointerUp);
      document.body.style.userSelect = "";
    };
  }, []);

  const handleMasterCreation = () => {
    setShowAudioEngineerModal(true)
    // const ids = Array.isArray(taskIds) ? taskIds : [];
    // dispatch(fetchFinalMasteredTracks(toast, { taskIds: ids }, setIsProcessing))
  }

  return (
    <div className="bg-white flex flex-col items-center gap-4 relative max-w-[700px] mx-auto rounded-2xl p-4 lg:p-5">
      <h2 className="text-[#FA6138] text-base font-semibold text-center">{demoMode || demoModeMaster ? "Track Name.mp3" : extractFileName(inputUri)}</h2>

      {/* Track selector */}
      {previewArray && previewArray.length > 0 && (
        <div className="w-full flex justify-center">
          <select
            aria-label="Select track preview"
            value={selectedPreviewIndex}
            disabled={previewArray.length <= 1}
            onChange={(e) => setSelectedPreviewIndex(Number(e.target.value))}
            className="appearance-none bg-white text-theme_clr1 text-base rounded-lg border border-2 border-theme_clr1 font-manrope border-0 focus:outline-none pl-4 pr-8"
          >
            {previewArray.map((item, idx) => (
              <option key={idx} value={idx}>{item?.data?.filename || item?.filename}</option>
            ))}
          </select>
        </div>
      )}

      {/* Circular Player */}
      <div className="relative w-[200px] h-[200px]">
        <svg ref={svgRef} className="w-full h-full" viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} aria-hidden>
          {/* static orange ring */}
          <circle cx={CX} cy={CY} r={RADIUS} stroke="#FA6138" strokeWidth="6" fill="none" />

          {/* subtle background track */}
          <circle cx={CX} cy={CY} r={RADIUS} stroke="rgba(26,26,58,0.08)" strokeWidth="2" fill="none" />

          {/* draggable pointer */}
          <circle
            cx={pointerX}
            cy={pointerY}
            r={9}
            fill="#FA6138"
            stroke="#fff"
            strokeWidth="2"
            style={{ cursor: isDragging ? "grabbing" : "grab" }}
            onMouseDown={onPointerDown}
            onTouchStart={onPointerDown}
            filter="url(#glow)"
          />

          {/* glow definition */}
          <defs>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* center visual */}
          <g transform={`translate(${CX - 36}, ${CY - 36})`}>
            <rect rx={36} ry={36} width={72} height={72} fill="#00067e" />
          </g>
        </svg>

        {/* Center Play/Pause button */}
        <button
          onClick={togglePlay}
          aria-label={isPlaying ? "Pause" : "Play"}
          className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[130px] h-[130px] rounded-full flex items-center justify-center text-white"
          style={{ backgroundColor: "#00067e" }}
        >
          {isAudioLoading || isPlayInitiated ? (
            <div className="relative w-full h-full flex items-center justify-center">
              <div className="w-12 h-12 rounded-full border-4 border-theme_clr2 border-t-transparent animate-spin"></div>
            </div>
          ) : isPlaying ? (
            <Pause className="w-8 h-8" />
          ) : (
            <Play className="w-8 h-8" />
          )}
        </button>
      </div>

      {/* Audio element */}
      <audio
        ref={audioRef}
        src={isMaster ? previewUrl : originalUrls}
        preload="metadata"
        onError={(e) => {
          console.error("Audio load error", e);
          setError("Failed to load audio");
        }}
      />

      {/* Controls (Original/Master, Volume match, Style, Loudness) */}
      <div className="space-y-4 w-full max-w-md">
        <div className="flex items-center justify-center gap-2">
          <span
            className={`px-1 py-0.5 rounded font-medium transition-colors ${!isMaster ? "text-theme_clr1" : "text-theme_clr2"
              }`}
          >
            Original
          </span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={isMaster}
              onChange={() => setIsMaster((s) => !s)}
            />
            <div className={`w-14 h-7 rounded-full relative transition-colors
                  ${isMaster ? "bg-[#FA6138]" : "bg-[#00067e]"}`}>
              <div
                className={`absolute top-[2px] left-[2px] w-6 h-6 bg-white rounded-full transition-transform ${isMaster ? "translate-x-7" : "translate-x-0"}`}
              />
            </div>
          </label>
          <span className="text-theme_clr2">Master</span>
        </div>
      </div>
      {allMastered || demoModeMaster ? (
        <DropdownMenu
          button={
            <button disabled={isDownloading} className="mt-6 w-[50vw] max-w-md bg-theme_clr2 text-white py-3 rounded-xl hover:bg-[#ff724d] transition-colors font-medium flex items-center justify-center gap-2">
              {isDownloading ? (
                <div className="relative w-6 h-6 flex items-center justify-center">
                  <div className="w-6 h-6 rounded-full border-2 border-[#FFFFFF] border-t-transparent animate-spin"></div>
                </div>
              ) : (
                <>
                  <span className="flex items-center justify-center h-6 w-6">
                    <Download size={16} />
                  </span>
                  Download Master
                </>
              )}
            </button>
          }
          fullWidth={true}
          items={getDownloadItems(selectedTrack)}
        />
      ) : (
        <button onClick={handleMasterCreation} disabled={isProcessing} className="mt-6 w-full max-w-md bg-[#FA6138] text-white py-3 rounded-xl hover:bg-[#ff724d] transition-colors font-medium">
          {isProcessing ? 'Processing your Mastered Track(s)...' : 'Get your release-ready master'}
        </button>
      )}

      {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
      <AudioEngineerPackModal
        isOpen={showAudioEngineerModal}
        onClose={() => setShowAudioEngineerModal(false)}
        masterRequestData={{
          fileName,
          fileUrl: originalUrl,
          inputUri,
          genre,
          loudness,
          sampleRate,
          format
        }}
        setIsProcessing={setIsProcessing}
      />
    </div>
  );
}

